import React from 'react';
import { PLAN_PRICING } from '../../services/SubscriptionPrices';

type PlanType = 'Free' | 'Plus' | 'Pro' | 'Topup';

interface PricingCardProps {
    billingPeriod: string;
    isCurrentPlan?: (planType: string) => boolean;
    paymentLoading?: string | null;
    onPlanAction?: (plan: PlanType) => void;
    variant?: 'upgrade' | 'subscription'; // 'upgrade' for UpgradePlans, 'subscription' for Subscription page
}

const PricingCards: React.FC<PricingCardProps> = ({
    billingPeriod,
    isCurrentPlan = () => false,
    paymentLoading = null,
    onPlanAction = () => { },
    variant = 'upgrade',
}) => {
    const getFeatureIcon = () => (
        <svg className="flex-shrink-0 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
    );

    const getFeatureIconLarge = () => (
        <svg className="flex-shrink-0 h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
    );

    const plans = [
        {
            name: 'Free',
            type: 'free' as const,
            price: '$0 or ₹0',
            period: 'forever',
            features: [
                'Basic AI Photoshoot (5 credits)',
                'Standard Virtual Try-On',
                variant === 'upgrade' ? 'Basic Gallery Storage' : 'Basic Gallery Storage (10 images)',
                'Community Support',
            ],
            buttonText: variant === 'upgrade' ? 'Current Plan' : 'Free Forever',
            buttonClass: variant === 'upgrade' ? 'bg-gray-600 cursor-not-allowed' : 'bg-gray-600',
            popular: false,
            description: 'Basic features for getting started',
        },
        {
            name: 'Plus',
            type: 'plus' as const,
            price: billingPeriod === 'monthly'
                ? `$${PLAN_PRICING.plus_monthly.usd} or ₹${PLAN_PRICING.plus_monthly.inr}`
                : billingPeriod === '4month'
                    ? `$${PLAN_PRICING.plus_4months.usd} or ₹${PLAN_PRICING.plus_4months.inr}`
                    : `$${PLAN_PRICING.plus_yearly.usd} or ₹${PLAN_PRICING.plus_yearly.inr}`,
            period: billingPeriod === 'monthly' ? 'per month' : billingPeriod === '4month' ? '4 months' : 'year',
            originalPrice: billingPeriod === '4month'
                ? `$${PLAN_PRICING.plus_monthly.usd * 4} or ₹${PLAN_PRICING.plus_monthly.inr * 4}`
                : billingPeriod === 'yearly'
                    ? `$${PLAN_PRICING.plus_monthly.usd * 12} or ₹${PLAN_PRICING.plus_monthly.inr * 12}`
                    : null,
            savings: billingPeriod === '4month' ? 'Save 15%' : billingPeriod === 'yearly' ? 'Save 20%' : null,
            features: [
                `Advanced AI Photoshoot (${billingPeriod === 'monthly'
                    ? `${PLAN_PRICING.plus_monthly.credits} credits/month`
                    : billingPeriod === '4month'
                        ? `${PLAN_PRICING.plus_4months.credits} credits total`
                        : `${PLAN_PRICING.plus_yearly.credits} credits total`})`,
                'Enhanced Try-On',
                'Extended Gallery Storage (100 images)',
                'Priority Support',
                'Outfit Extractor (Flatlay)',
            ],
            buttonText: 'Upgrade to Plus',
            buttonClass: 'bg-secondary-500 hover:bg-secondary-600',
            popular: false,
            description: 'Perfect for regular users',
        },
        {
            name: 'Pro',
            type: 'pro' as const,
            price: billingPeriod === 'monthly'
                ? `$${PLAN_PRICING.pro_monthly.usd} or ₹${PLAN_PRICING.pro_monthly.inr}`
                : billingPeriod === '4month'
                    ? `$${PLAN_PRICING.pro_4months.usd} or ₹${PLAN_PRICING.pro_4months.inr}`
                    : `$${PLAN_PRICING.pro_yearly.usd} or ₹${PLAN_PRICING.pro_yearly.inr}`,
            period: billingPeriod === 'monthly' ? 'per month' : billingPeriod === '4month' ? '4 months' : 'year',
            originalPrice: billingPeriod === '4month'
                ? `$${PLAN_PRICING.pro_monthly.usd * 4} or ₹${PLAN_PRICING.pro_monthly.inr * 4}`
                : billingPeriod === 'yearly'
                    ? `$${PLAN_PRICING.pro_monthly.usd * 12} or ₹${PLAN_PRICING.pro_monthly.inr * 12}`
                    : null,
            savings: billingPeriod === '4month' ? 'Save 15%' : billingPeriod === 'yearly' ? 'Save 20%' : null,
            features: [
                `Advanced AI Photoshoot (${billingPeriod === 'monthly'
                    ? `${PLAN_PRICING.pro_monthly.credits} credits/month`
                    : billingPeriod === '4month'
                        ? `${PLAN_PRICING.pro_4months.credits} credits total`
                        : `${PLAN_PRICING.pro_yearly.credits} credits total`})`,
                'Premium Try-On',
                'Unlimited Gallery Storage',
                '24/7 Priority Support',
                'Outfit Extractor (Flatlay)'
            ],
            buttonText: 'Upgrade to Pro',
            buttonClass: 'bg-secondary-500 hover:bg-secondary-600',
            popular: true,
            description: 'For power users and professionals',
        },
        {
            name: 'Topup',
            type: 'topup' as const,
            price: billingPeriod === 'monthly'
                ? `$${PLAN_PRICING.topup_monthly.usd} or ₹${PLAN_PRICING.topup_monthly.inr}`
                : billingPeriod === '4month'
                    ? `$${PLAN_PRICING.topup_4months.usd} or ₹${PLAN_PRICING.topup_4months.inr}`
                    : `$${PLAN_PRICING.topup_yearly.usd} or ₹${PLAN_PRICING.topup_yearly.inr}`,
            period: 'one-time',
            originalPrice: billingPeriod === '4month'
                ? `$${PLAN_PRICING.topup_monthly.usd * 4} or ₹${PLAN_PRICING.topup_monthly.inr * 4}`
                : billingPeriod === 'yearly'
                    ? `$${PLAN_PRICING.topup_monthly.usd * 12} or ₹${PLAN_PRICING.topup_monthly.inr * 12}`
                    : null,
            savings: billingPeriod === '4month' ? 'Save 15%' : billingPeriod === 'yearly' ? 'Save 20%' : null,
            features: [
                billingPeriod === 'monthly'
                    ? `${PLAN_PRICING.topup_monthly.credits} Credits Instantly`
                    : billingPeriod === '4month'
                        ? `${PLAN_PRICING.topup_4months.credits} Credits Instantly`
                        : `${PLAN_PRICING.topup_yearly.credits} Credits Instantly`,
                billingPeriod === 'monthly' ? 'No Monthly Commitment' : 'No Subscription Commitment',
                'Credits Never Expire',
                ...(billingPeriod !== 'monthly' ? ['Bulk Credit Savings'] : []),
            ],
            buttonText: 'Buy Credits',
            buttonClass: 'bg-green-600 hover:bg-green-700',
            popular: false,
            description: `No ${billingPeriod === 'monthly' ? 'monthly' : 'subscription'} commitment needed`,
        },
    ];

    const getButtonContent = (plan: typeof plans[0]) => {
        const isLoading = paymentLoading === plan.name;
        const isCurrent = isCurrentPlan(plan.type);

        if (variant === 'subscription') {
            if (plan.name === 'Free') {
                return isCurrent ? 'Current Plan' : 'Free Forever';
            }

            if (isCurrent) {
                return 'Current Plan';
            }

            if (isLoading) {
                return (
                    <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Processing...</span>
                    </>
                );
            }

            return plan.buttonText;
        }

        // variant === 'upgrade'
        if (plan.name === 'Free') {
            return plan.buttonText;
        }

        return isLoading ? 'Processing...' : plan.buttonText;
    };

    const getButtonProps = (plan: typeof plans[0]) => {
        const isLoading = paymentLoading === plan.name;
        const isCurrent = isCurrentPlan(plan.type);

        if (variant === 'subscription') {
            if (plan.name === 'Free') {
                return {
                    disabled: !isCurrent,
                    className: `w-full py-3 px-4 rounded-lg font-medium ${isCurrent
                        ? 'bg-secondary-500 text-white cursor-default'
                        : 'bg-gray-600 text-white cursor-not-allowed'
                        }`,
                };
            }

            if (isCurrent) {
                return {
                    disabled: true,
                    className: 'w-full bg-secondary-500 text-white py-3 px-4 rounded-lg cursor-default font-medium',
                };
            }

            return {
                disabled: isLoading,
                onClick: () => onPlanAction(plan.name as PlanType),
                className: `w-full ${plan.buttonClass} text-white py-3 px-4 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center space-x-2 font-medium`,
            };
        }

        // variant === 'upgrade'
        if (plan.name === 'Free') {
            return {
                disabled: true,
                className: `block w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-center text-base font-medium text-white ${plan.buttonClass}`,
            };
        }

        return {
            disabled: isLoading,
            onClick: () => onPlanAction(plan.name as PlanType),
            className: `block w-full py-2 sm:py-3 px-3 sm:px-4 border border-transparent rounded-md shadow-sm text-center text-sm sm:text-base font-medium text-white ${plan.buttonClass} ${isLoading ? 'opacity-75 cursor-not-allowed' : ''
                }`,
        };
    };

    const cardClassName = (plan: typeof plans[0]) => {
        const baseClass = 'border rounded-lg flex flex-col h-full transition-all duration-200 hover:shadow-md';
        const currentClass = variant === 'subscription' && isCurrentPlan(plan.type)
            ? 'border-secondary-500 bg-secondary-100'
            : '';
        const popularClass = plan.popular && !isCurrentPlan(plan.type)
            ? 'border-secondary-500 border-2 shadow-lg'
            : 'border-accent-taupe';
        const shadowClass = variant === 'upgrade' ? 'shadow-sm' : '';

        return `${baseClass} ${currentClass || popularClass} ${shadowClass} ${plan.name === 'Pro' ? 'relative' : ''}`;
    };

    return (
        <div className={`grid gap-6 ${variant === 'upgrade'
            ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
            : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
            }`}>
            {plans.map((plan) => (
                <div key={plan.name} className={cardClassName(plan)}>
                    {plan.popular && !isCurrentPlan(plan.type) && (
                        <div className="absolute top-0 right-0 bg-secondary-1000 text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg">
                            MOST POPULAR
                        </div>
                    )}
                    <div className="p-4 sm:p-6 flex flex-col h-full">
                        <div>
                            <h4 className={`font-semibold mb-2 ${variant === 'upgrade' ? 'text-lg leading-6 text-text-heading mb-4' : 'text-lg'}`}>
                                {plan.name === 'Topup' ? 'Credit Topup' : `${plan.name} Plan`}
                            </h4>
                            <div className="mb-4">
                                {variant === 'upgrade' ? (
                                    <>
                                        <p className="text-3xl sm:text-4xl font-extrabold text-text-heading">
                                            {plan.price}
                                        </p>
                                        <span className="text-sm sm:text-base font-medium text-text-description">
                                            /{plan.period}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <div className={`font-bold text-text-heading ${plan.name === 'Free' ? 'text-2xl sm:text-3xl' : 'text-2xl sm:text-3xl'}`}>
                                            {plan.price}
                                        </div>
                                        <span className="text-sm sm:text-base font-medium text-text-description">
                                            /{plan.period}
                                        </span>
                                    </>
                                )}

                                {/* Original Price and Savings */}
                                {plan.originalPrice && (
                                    <>
                                        <p className="text-sm text-text-description line-through mt-1">
                                            {plan.originalPrice}
                                        </p>
                                        <p className={`text-sm font-medium mt-1 ${billingPeriod === '4month' ? 'text-green-600' : 'text-blue-600'}`}>
                                            {plan.savings}
                                        </p>
                                    </>
                                )}
                            </div>
                            <p className="text-text-description text-sm mb-4">{plan.description}</p>
                        </div>

                        <ul className={`space-y-2 text-xs sm:text-sm text-text-description mb-4 sm:mb-6 flex-grow ${variant === 'upgrade' ? 'mt-1 space-y-2 sm:space-y-3' : ''}`}>
                            {plan.features.map((feature) => (
                                <li key={feature} className="flex items-start">
                                    {variant === 'upgrade' ? getFeatureIconLarge() : getFeatureIcon()}
                                    <span className={`${variant === 'upgrade' ? 'ml-2 sm:ml-3 text-sm sm:text-base text-text-description' : 'ml-2 sm:ml-3'}`}>
                                        {feature}
                                    </span>
                                </li>
                            ))}
                        </ul>

                        <div className={variant === 'upgrade' ? 'mt-6 sm:mt-8' : 'mt-auto'}>
                            <button {...getButtonProps(plan)}>
                                {getButtonContent(plan)}
                            </button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default PricingCards;
